import { Typography } from '@mui/material'
import { Collaborators } from 'Components/_common/Collaborators/Collaborators'
import { ThemesBlock } from 'Components/SubmitMap/PreviewStep/ThemesBlock'
import { usePrimaryGameType } from 'Queries/Dictionaries'
import { PROFILE_IMAGE_TYPE } from 'Queries/utils'
import { useState } from 'react'
import { Else, If, Then, When } from 'react-if'
import Box from 'Theme/Box'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'
import { getFullDateInDetails } from 'utils/date-utils'
import { getTotalCount, getTotalLikes } from 'utils/total-count-utils'
import { userImageSelector } from 'utils/user-images-utils'

import ContentEntityDetails from '../_common/ContentEntityDetails'
import ContentSoftPublishPlate from '../_common/ContentPreview/ContentSoftPublishPlate'
import DescriptionBlock from '../_common/CreationForm/DescriptionBlock'
import GameTypeIcon from '../_common/GameTypeIcon'
import UserCard from '../_common/UserCard'
import ContestGameTypes from '../ContestComponents/ContestGameTypes'
import ContestJudgesList from '../ContestComponents/ContestJudgesList'
import ContestRulesInfo from '../ContestComponents/ContestRulesInfo'
import { plainTextTransformation } from '../ContestComponents/utils'
import GameTypesList from '../MapsComponents/GameTypesList'
import MainSwipableDrawer from './MainSwipableDrawer'

const previewIconStyles = {
  variant: 'text',
  color: 'inherit',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '7px',
}

export const MobileAbout = ({
  owner,
  ownerMapsUploaded,
  gameName,
  content,
  gameTypeNames,
  isContest,
  isJudge = false,
  isPublishedRound = false,
  judges,
  mapPlayers,
  categoryName,
  type,
  isPreview,
  themes,
  contestRelation,
  haloWaypointUser,
  isHaloWaypoint,
}) => {
  const [isOpenDrawer, setIsOpenDrawer] = useState(false)
  const primaryGameType = usePrimaryGameType(content.primary_game_type_id)
  const totalLikes = getTotalLikes(content)
  const waypointUsers = content.relations?.halowaypoint_users
    ? Object.values(content.relations?.halowaypoint_users)
    : []

  return (
    <>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.background.dark,
          borderRadius: '15px',
          padding: '20px',
        })}
        onClick={() => setIsOpenDrawer(true)}
      >
        <When condition={isContest}>
          <Typography
            variant="h6"
            color="text.primary"
          >
            About
          </Typography>
        </When>

        <When condition={!isContest}>
          <Box
            display="flex"
            justifyContent="space-between"
          >
            <Typography sx={previewIconStyles}>
              <FAIcon
                iconClassName={FA_ICONS.EYE}
                color="text.tertiary"
              />
              {getTotalCount(content.views)}
            </Typography>

            <Typography sx={previewIconStyles}>
              <When condition={content.plays_recent >= 0}>
                <>
                  <FAIcon
                    iconClassName={FA_ICONS.CLOCK_ROTATE_LEFT}
                    color="text.tertiary"
                  />
                  {getTotalCount(content.plays_recent)}
                </>
              </When>
            </Typography>

            <Typography sx={previewIconStyles}>
              <When condition={content.plays_total >= 0}>
                <>
                  <FAIcon
                    iconClassName={FA_ICONS.PLAY}
                    color="text.tertiary"
                  />
                  {getTotalCount(content.plays_total)}
                </>
              </When>
            </Typography>

            <Typography sx={previewIconStyles}>
              <FAIcon
                iconClassName={FA_ICONS.HEART}
                color="text.tertiary"
              />
              {getTotalCount(totalLikes)}
            </Typography>

            <Typography sx={previewIconStyles}>
              <FAIcon
                iconClassName={FA_ICONS.BOOKMARK}
                color="text.tertiary"
              />
              {getTotalCount(content.bookmarks_total)}
            </Typography>
          </Box>
        </When>

        <Box overflow="hidden">
          <Box mt={3}>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                width: '100%',
                overflowWrap: 'break-word',
              }}
            >
              {plainTextTransformation(content.description)}
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          mt="12px"
        >
          {gameTypeNames?.map((gameType, index) => (
            <GameTypeIcon
              key={index}
              size={'small'}
              gameType={gameType}
              color="text.tertiary"
            />
          ))}
        </Box>
      </Box>
      <MainSwipableDrawer
        isOpenDrawer={isOpenDrawer}
        setIsOpenDrawer={setIsOpenDrawer}
        drawerHeight={'75vh'}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px="32px"
          mt={4}
        >
          <Typography
            variant="h5"
            color="text.primary"
          >
            About
          </Typography>
          <FAIcon
            iconClassName={FA_ICONS.CLOSE}
            color="text.tertiary"
            onClick={() => setIsOpenDrawer(false)}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          p={4}
        >
          <When condition={!isContest && (!!owner.login || !!haloWaypointUser)}>
            <UserCard
              id={owner.id}
              alt={owner.login}
              src={userImageSelector(owner.user_images, PROFILE_IMAGE_TYPE.AVATAR, 'medium')}
              name={owner.login}
              description={owner.firstName}
              mapsCount={ownerMapsUploaded}
              flexDirection={'row !important'}
              justifyContent={'space-between'}
              alignItems={'center'}
              width={'100%'}
              isHaloWaypoint={isHaloWaypoint}
              haloWaypointUser={haloWaypointUser}
            />
          </When>

          <DescriptionBlock
            description={content.description}
            isMobile
          />

          <ContentSoftPublishPlate
            isShow={!!content?.is_soft_published}
            haloWaypointUser={haloWaypointUser}
          />

          {isContest ? (
            <ContestGameTypes gameTypeNames={gameTypeNames} />
          ) : (
            <Box>
              <GameTypesList gameTypeNames={gameTypeNames} />
            </Box>
          )}
          <If condition={isContest}>
            <Then>
              <ContestRulesInfo
                gameTitle={gameName}
                categoryName={categoryName}
                mapPlayers={mapPlayers}
                submissionDeadline={content.time_add_maps_end}
                mapsPerAuthor={content.max_maps_count}
                isRemakesAllowed={content.is_remakes_allowed}
                isMobile
              />
            </Then>
            <Else>
              <ContentEntityDetails
                recentlyPlayed={content.plays_recent}
                totalPlayed={content.plays_total}
                timeUpdated={getFullDateInDetails(content.time_updated)}
                timePublished={getFullDateInDetails(content.time_published)}
                gameTitle={gameName}
                gameTypeNames={primaryGameType ? [primaryGameType.name] : gameTypeNames}
                remakeId={content.remake_id}
                playersMin={content.players_min}
                playersMax={content.players_max}
                type={type}
                contentRatings={content.rating}
                contentId={content.id}
              />
            </Else>
          </If>
          <When condition={isContest}>
            <ContestJudgesList
              judges={judges}
              contest={content}
              isOwner={content.is_owner}
              isJudge={isJudge}
              isPublishedRound={isPublishedRound}
              contestId={content.id}
              ownerId={content.owner_id}
              contestRelation={contestRelation}
            />
          </When>
          <When condition={!isContest}>
            {!!content.collaborators?.length && (
              <Box>
                <Typography variant="h5">Collaborators</Typography>
                <Collaborators
                  collaborators={content.collaborators}
                  isPreview={isPreview}
                  waypointUsers={waypointUsers}
                  gap={2}
                />
              </Box>
            )}
            {!!themes?.length && <ThemesBlock themes={themes} />}
          </When>
        </Box>
      </MainSwipableDrawer>
    </>
  )
}
